import { useState } from 'react'
import { MAX_SKILL_HINT_LENGTH, MAX_SKILL_NAME_LENGTH } from 'src/feature/dashboard/constants/skill-assessment-constants'
import { SkillSetLevel } from 'src/feature/skill-assessment/model/skill-assessment-model'

export type EditModeState = {
  isEditMode: boolean
  editedDescriptions: Record<string, string>
  editedSkillName: string
  editedSkillHint: string
}

export type EditModeActions = {
  toggleEditMode: (levels: SkillSetLevel[], currentName: string, currentHint: string) => void
  resetEditMode: () => void
  updateDescription: (levelId: string, value: string, maxLength: number) => void
  updateSkillName: (value: string) => void
  updateSkillHint: (value: string) => void
}

/**
 * Custom hook for managing edit mode state and actions
 */
export function useEditMode(): [EditModeState, EditModeActions] {
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedDescriptions, setEditedDescriptions] = useState<Record<string, string>>({})
  const [editedSkillName, setEditedSkillName] = useState('')
  const [editedSkillHint, setEditedSkillHint] = useState('')

  const toggleEditMode = (levels: SkillSetLevel[], currentName: string, currentHint: string) => {
    if (isEditMode) {
      setEditedDescriptions({})
      setEditedSkillName('')
      setEditedSkillHint('')
    } else {
      const initialDescriptions: Record<string, string> = {}

      levels.forEach((level) => {
        initialDescriptions[level.id] = level.setLevelDescription
      })
      setEditedDescriptions(initialDescriptions)
      setEditedSkillName(currentName)
      setEditedSkillHint(currentHint)
    }
    setIsEditMode(!isEditMode)
  }

  const resetEditMode = () => {
    setIsEditMode(false)
    setEditedDescriptions({})
    setEditedSkillName('')
    setEditedSkillHint('')
  }

  const updateDescription = (levelId: string, value: string, maxLength: number) => {
    if (value.length <= maxLength) {
      setEditedDescriptions(prev => ({
        ...prev,
        [levelId]: value,
      }))
    }
  }

  const updateSkillName = (value: string) => {
    if (value.length <= MAX_SKILL_NAME_LENGTH) {
      setEditedSkillName(value)
    }
  }

  const updateSkillHint = (value: string) => {
    if (value.length <= MAX_SKILL_HINT_LENGTH) {
      setEditedSkillHint(value)
    }
  }

  return [
    {
      isEditMode,
      editedDescriptions,
      editedSkillName,
      editedSkillHint,
    },
    {
      toggleEditMode,
      resetEditMode,
      updateDescription,
      updateSkillName,
      updateSkillHint,
    },
  ]
}
